import * as React from 'react';
import type { DataValue } from 'react-apollo';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Typography2 } from '@coursera/cds-core';
import { Box, Checkbox, Loading } from '@coursera/coursera-ui';

import { Spacer } from 'bundles/enterprise-ui/components';
import type {
  AcademicIntegrityQuery,
  AcademicIntegrityQueryVariables,
  AcademicIntegrityQuery_EnterpriseSettingsV1Resource_byOrganization_elements as Setting,
} from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/__generated__/AcademicIntegrityQuery';
import {
  GRADEBOOK_PLAGIARISM_COLUMNS_ENABLED,
  getOrganizationLevelBooleanSettingValue,
  updateOrganizationLevelBooleanSetting,
} from 'bundles/internal-enterprise/utils/enterpriseSettingsUtils';

import _t from 'i18n!nls/internal-enterprise';

type PropsFromCaller = {
  thirdPartyOrganizationId?: string;
};

type PropsFromGraphql = {
  data: DataValue<AcademicIntegrityQuery, AcademicIntegrityQueryVariables>;
};

type Props = PropsFromCaller & PropsFromGraphql;

const AcademicIntegrity: React.FC<Props> = ({ thirdPartyOrganizationId, data }) => {
  if (!thirdPartyOrganizationId) {
    return <Loading />;
  }

  const settings = data.EnterpriseSettingsV1Resource?.byOrganization?.elements as Setting[];

  return (
    <div>
      <Box alignItems="center">
        <Typography2 component="p">
          {_t('Include Information about Confirmed Plagiarism Incidents in the Gradebook:')}
        </Typography2>
        <Spacer size={12} />
        <Checkbox
          htmlAttributes={{
            'data-e2e': 'org-level-gradebook-plagiarism',
          }}
          disabled={data.loading}
          indeterminate={data.loading}
          checked={getOrganizationLevelBooleanSettingValue(
            GRADEBOOK_PLAGIARISM_COLUMNS_ENABLED,
            thirdPartyOrganizationId,
            settings
          )}
          onChange={(event) => {
            updateOrganizationLevelBooleanSetting({
              id: thirdPartyOrganizationId,
              setting: GRADEBOOK_PLAGIARISM_COLUMNS_ENABLED,
              isEnabled: event.target.checked,
            }).then(() => data.refetch());
          }}
        />
      </Box>
      <FormattedMessage
        message={_t(`The below plagiarism features are now managed using {features}.`)}
        features={
          <Link href="/internal/feature-toggles" target="_blank" rel="noopener noreferrer">
            {_t('Product Features')}
          </Link>
        }
      />
      <Spacer size={12} />
      <ul>
        <li>
          <strong>{_t(`Anonymous Peer Review`)}</strong>: enterprise - isAnonymousPeerReviewEnabled
        </li>
        <li>
          <strong>{_t(`Disable Peer Review URL Sharing`)}</strong>: enterprise - isDisallowPeerReviewUrlSharingEnabled
        </li>
      </ul>
    </div>
  );
};

export const QueryForAcademicIntegrity = gql`
  query AcademicIntegrityQuery($thirdPartyOrganizationId: String!) {
    EnterpriseSettingsV1Resource {
      byOrganization(thirdPartyOrganizationId: $thirdPartyOrganizationId, includeProgramSettings: true) {
        elements {
          id
          setting {
            ... on EnterpriseSettingsV1_booleanContentMember {
              booleanContent {
                value
              }
            }
            ... on EnterpriseSettingsV1_integerContentMember {
              integerContent {
                value
              }
            }
            ... on EnterpriseSettingsV1_stringContentMember {
              stringContent {
                value
              }
            }
            ... on EnterpriseSettingsV1_stringArrayContentMember {
              stringArrayContent {
                value
              }
            }
            ... on EnterpriseSettingsV1_stringMapContentMember {
              stringMapContent {
                value
              }
            }
          }
        }
      }
    }
  }
`;

export default compose<Props, PropsFromCaller>(
  graphql<PropsFromCaller, AcademicIntegrityQuery, AcademicIntegrityQueryVariables, PropsFromGraphql>(
    QueryForAcademicIntegrity,
    {
      skip: (props) => !props.thirdPartyOrganizationId,
      options: ({ thirdPartyOrganizationId }) => ({
        variables: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          thirdPartyOrganizationId: thirdPartyOrganizationId!,
        },
      }),
    }
  )
)(AcademicIntegrity);
